/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { Highlight } from 'ui/atoms/Highlight/Highlight';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './WaysToEnrichSection.module.scss';

import { WAYS_TO_ENRICH } from './WaysToEnrichSection.data';

// A card counts as "active" once it scrolls up to this line. Fixed rather than derived
// from the stage's own rect — the stage sits below the sticky intro block, so its rect
// would drift with the intro's height and skip past the first couple of cards before
// the user ever sees them active. Matches `.stageWrap { top }` + `.list { padding-top }`
// in the module scss, i.e. sits roughly where the stage begins — keeping it that low
// (rather than up near the header) means each card only has to travel a short way up
// the screen to switch, instead of scrolling almost to the top edge.
const ACTIVE_LINE = 112 + 300;

export const WaysToEnrichSection = () => {
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [active, setActive] = useState(0);

  useEffect(() => {
    let raf = 0;

    const pick = () => {
      raf = 0;

      let best = 0;
      for (let i = 0; i < itemRefs.current.length; i++) {
        const el = itemRefs.current[i];
        if (!el) continue;

        const r = el.getBoundingClientRect();
        if ((r.top + r.bottom) / 2 <= ACTIVE_LINE) {
          best = i;
        } else {
          break;
        }
      }

      setActive(best);
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(pick);
    };

    requestAnimationFrame(pick);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    window.addEventListener('load', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('load', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const select = (index: number) => {
    const el = itemRefs.current[index];
    if (!el) return;

    const elRect = el.getBoundingClientRect();
    const elCenter = (elRect.top + elRect.bottom) / 2;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: window.scrollY + elCenter - ACTIVE_LINE,
      behavior: reduced ? 'auto' : 'smooth',
    });
  };

  return (
    <Section>
      <div className={styles.grid}>
        <div className={styles.stageWrap}>
          <TitleBlock
            align="left"
            className={styles.introBlock}
            extra={
              <Highlight color="blue" font="mono" uppercase>
                Ways to enrich
              </Highlight>
            }
            title="Six ways to get registry data into your systems"
            description="License it in bulk, upload a file, call the API, install a CRM app, connect an AI assistant, or hand the batch to us."
          />

          <div className={styles.stage}>
            {WAYS_TO_ENRICH.map((way, i) => {
              const Media = way.media;

              return (
                <div
                  key={way.label}
                  className={cn(styles.panel, i === active && styles.panelActive)}
                  aria-hidden={i !== active}
                >
                  <Media active={i === active} />
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.list} role="tablist" aria-label="Ways to enrich">
          {WAYS_TO_ENRICH.map((way, i) => {
            const on = i === active;

            return (
              <button
                key={way.label}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                aria-selected={on}
                className={cn(styles.item, on && styles.itemActive)}
                onClick={() => select(i)}
              >
                <span className={styles.itemLabel}>{way.label}</span>
                <span className={styles.itemTitle}>{way.title}</span>
                <span className={styles.itemDesc}>{way.description}</span>
              </button>
            );
          })}
        </div>
      </div>
    </Section>
  );
};
